import React, { useState, useEffect, useRef } from 'react';
import {
    Card, CardBody, Container, Row, Col, FormGroup, Label, Input, Button
} from 'reactstrap';
import requestApi from '../../../services/requestApi.service';
import { showToast, uploadFileIntoS3 } from '../../../Common/Appcommon';

const CreateRCSTemplate = ({ onCancel }) => {
    // Global Settings
    const [templateType, setTemplateType] = useState('Rich Media Card');
    const [botName, setBotName] = useState('');
    const [templateName, setTemplateName] = useState('');
    const [ttl, setTtl] = useState('');

    // Input Refs for cursor position
    const titleInputRef = useRef(null);
    const bodyInputRef = useRef(null);
    const [lastCursorPos, setLastCursorPos] = useState({ title: null, body: null });


    // Card Style Settings
    const [orientation, setOrientation] = useState('vertical'); // For Rich Media
    const [cardHeight, setCardHeight] = useState('short'); // For all
    const [cardWidth, setCardWidth] = useState('small'); // For Carousel

    const [errors, setErrors] = useState({});

    // Card Content State
    const [activeCardIndex, setActiveCardIndex] = useState(0);
    const [cards, setCards] = useState([
        {
            mediaType: 'image',
            title: '',
            body: '',
            suggestions: [],
            imageFile: null, imageFileName: '',
            videoFile: null, videoFileName: '',
            videoThumbnailFile: null, videoThumbnailFileName: ''
        }
    ]);

    // Helpers
    const handleAddCard = () => {
        if (cards.length < 10) {
            setCards([...cards, {
                mediaType: 'image',
                title: '',
                body: '',
                suggestions: [],
                imageFile: null, imageFileName: '',
                videoFile: null, videoFileName: '',
                videoThumbnailFile: null, videoThumbnailFileName: ''
            }]);
            setActiveCardIndex(cards.length);
        }
    };

    const handleRemoveCard = (index, e) => {
        e.stopPropagation();
        if (cards.length > 2) { // Min 2 cards for carousel
            const newCards = cards.filter((_, i) => i !== index);
            setCards(newCards);
            if (activeCardIndex >= newCards.length) {
                setActiveCardIndex(newCards.length - 1);
            }
        }
    };

    const updateCurrentCard = (field, value) => {
        const newCards = [...cards];
        newCards[activeCardIndex] = { ...newCards[activeCardIndex], [field]: value };
        setCards(newCards);
    };

    const updateButton = (btnIndex, field, value) => {
        const newCards = [...cards];
        const newSuggestions = [...newCards[activeCardIndex].suggestions];
        newSuggestions[btnIndex] = { ...newSuggestions[btnIndex], [field]: value };
        newCards[activeCardIndex].suggestions = newSuggestions;
        setCards(newCards);
    };

    const addButton = () => {
        const currentCard = cards[activeCardIndex];
        const currentSuggestions = currentCard.suggestions;
        if (currentSuggestions.length < 3) {
            const hasVar = /\[custom_param_text_\d+\]/.test(currentCard.title || "") || 
                           /\[custom_param_desc_\d+\]/.test(currentCard.body || "");
            
            // Default type: URL Action if others restricted, else Reply
            let defaultType = hasVar ? 'URL Action' : 'Reply';
            
            const newCards = [...cards];
            newCards[activeCardIndex].suggestions = [...currentSuggestions, { type: defaultType, text: '' }];
            setCards(newCards);
        }
    };

    const removeButton = (btnIndex) => {
        const newCards = [...cards];
        newCards[activeCardIndex].suggestions = newCards[activeCardIndex].suggestions.filter((_, i) => i !== btnIndex);
        setCards(newCards);
    };

    const validateImageDimensions = (file, expectedWidth, expectedHeight) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.src = URL.createObjectURL(file);
            img.onload = () => {
                URL.revokeObjectURL(img.src);
                if (img.width === expectedWidth && img.height === expectedHeight) {
                    resolve({ valid: true });
                } else {
                    resolve({ valid: false, width: img.width, height: img.height });
                }
            };
            img.onerror = () => resolve({ valid: false, error: 'Invalid image file' });
        });
    };

    const getExpectedDimensions = () => {
        if (templateType === 'Rich Media Card') {
            if (orientation === 'horizontal') return { width: 768, height: 1024 };
            if (orientation === 'vertical') {
                if (cardHeight === 'short') return { width: 1440, height: 480 };
                if (cardHeight === 'medium') return { width: 1440, height: 720 };
            }
        } else if (templateType === 'Carousel Card') {
            if (cardWidth === 'small') {
                if (cardHeight === 'short') return { width: 960, height: 768 };
                if (cardHeight === 'medium') return { width: 576, height: 720 };
            } else if (cardWidth === 'medium') {
                if (cardHeight === 'short') return { width: 1440, height: 720 };
                if (cardHeight === 'medium') return { width: 1440, height: 1080 };
            }
        }
        return null;
    };

    const handleFileChange = async (index, type, e) => {
        const file = e.target.files[0];
        if (file) {
            if (type === 'image') {
                const dims = getExpectedDimensions();
                if (dims) {
                    const result = await validateImageDimensions(file, dims.width, dims.height);
                    if (!result.valid) {
                        showToast(`Invalid image dimensions. Expected ${dims.width}x${dims.height}, but got ${result.width}x${result.height}`, "top-right", "error");
                        e.target.value = '';
                        return;
                    }
                }
            }

            // Perform S3 Upload
            const { resetInput, formInputData } = await uploadFileIntoS3(e, {});
            if (resetInput) {
                e.target.value = '';
                return;
            }

            const newCards = [...cards];
            if (type === 'image') {
                newCards[index].imageFile = file;
                newCards[index].imageFileName = file.name;
                newCards[index].imagePreviewUrl = formInputData.mediaUrl;
                newCards[index].imageS3Url = formInputData.mediaUrl;
                newCards[index].imageS3Handle = formInputData.media_handle;
            } else if (type === 'video') {
                newCards[index].videoFile = file;
                newCards[index].videoFileName = file.name;
                newCards[index].videoPreviewUrl = formInputData.mediaUrl;
                newCards[index].videoS3Url = formInputData.mediaUrl;
                newCards[index].videoS3Handle = formInputData.media_handle;
            } else if (type === 'videoThumbnail') {
                newCards[index].videoThumbnailFile = file;
                newCards[index].videoThumbnailFileName = file.name;
                newCards[index].videoThumbnailS3Url = formInputData.mediaUrl;
            }
            setCards(newCards);
        }
    };



    const handleCursorPosition = (e) => {
        const { name } = e.target;
        // Since Inputs don't have name prop set in my code above, I'll rely on active focus or identification
        // Actually, simple ref usage is better.
        // Let's check which input is active by ref check
        if (titleInputRef.current && titleInputRef.current === e.target) {
            setLastCursorPos({ ...lastCursorPos, title: e.target.selectionStart });
        } else if (bodyInputRef.current && bodyInputRef.current === e.target) {
            setLastCursorPos({ ...lastCursorPos, body: e.target.selectionStart });
        }
    };

    const addVariable = (field) => {
        const currentText = cards[activeCardIndex][field] || '';
        let prefix = '';
        let paramPattern = null;

        if (field === 'title') {
            prefix = 'custom_param_text_';
            paramPattern = /custom_param_text_(\d+)/g;
        } else {
            prefix = 'custom_param_desc_';
            paramPattern = /custom_param_desc_(\d+)/g;
        }

        let maxIndex = 0;
        let match;
        // Search globally for existing indices
        while ((match = paramPattern.exec(currentText)) !== null) {
            const index = parseInt(match[1], 10);
            if (index > maxIndex) maxIndex = index;
        }

        const newVariable = `[${prefix}${maxIndex + 1}]`;

        // Insert at cursor position
        let cursorPos = lastCursorPos[field];
        // If cursor pos is null or we are not focused, append to end? 
        // Or if we just clicked button, focus might imply end if not tracked.
        // Let's assume append if null, but try to track.
        if (cursorPos === null || cursorPos === undefined) {
            cursorPos = currentText.length;
        }

        // Safety: ensure cursorPos is within bounds
        if (cursorPos > currentText.length) cursorPos = currentText.length;

        const newText = currentText.slice(0, cursorPos) + newVariable + currentText.slice(cursorPos);

        updateCurrentCard(field, newText);

        // Update cursor position to be after inserted variable
        /* setTimeout(() => {
            if (field === 'title' && titleInputRef.current) {
                titleInputRef.current.focus();
                // titleInputRef.current.setSelectionRange(cursorPos + newVariable.length, cursorPos + newVariable.length);
            }
            if (field === 'body' && bodyInputRef.current) {
                 bodyInputRef.current.focus();
            }
        }, 0); */
        // Note: setting selection range immediately might be tricky with React state updates. 
        // We'll update the tracked cursor pos so next insert works correctly if they don't move cursor.
        setLastCursorPos({ ...lastCursorPos, [field]: cursorPos + newVariable.length });
    };


    const validateForm = () => {
        let newErrors = {};

        // Template Name
        if (!templateName) {
            newErrors.templateName = 'Template Name is required';
        } else if (templateName.length > 20) {
            newErrors.templateName = 'Max length 20 characters';
        } else if (!/^[A-Za-z0-9_]+$/.test(templateName)) {
            newErrors.templateName = 'Only alphanumeric and underscores allowed (no spaces)';
        }

        // Cards Validation
        cards.forEach((card, idx) => {
            const bodyMax = templateType === 'Standard Card' ? 2500 : 2000;
            
            if (templateType !== 'Standard Card' && !card.title) {
                newErrors[`title_${idx}`] = 'Title is required';
            }
            
            if (!card.body) {
                newErrors[`body_${idx}`] = 'Body description is required';
            } else if (card.body.length > bodyMax) {
                newErrors[`body_${idx}`] = `Body exceeds max length of ${bodyMax} chars`;
            }

            if (templateType !== 'Standard Card' && card.mediaType === 'image' && !card.imageS3Url) {
                newErrors[`image_${idx}`] = 'Image is required';
            }

            card.suggestions.forEach((btn, bidx) => {
                if (!btn.text) {
                    newErrors[`btn_text_${idx}_${bidx}`] = 'Button text is required';
                }
            });
        });

        // TTL Validation
        if (ttl && (parseInt(ttl) < 600 || parseInt(ttl) > 3600)) {
            newErrors.ttl = 'TTL value must be between 600 and 3600 seconds';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            showToast("Please fix validation errors before saving", "top-right", "error");
            return;
        }
        // Prepare cards with S3 URLs
        const processedCards = cards.map((card) => {
            let newCard = { ...card };
            if (newCard.imageS3Url) {
                newCard.imageContent = newCard.imageS3Url;
                newCard.imageHandle = newCard.imageS3Handle;
                delete newCard.imageFile;
                delete newCard.imagePreviewUrl;
                delete newCard.imageS3Url;
                delete newCard.imageS3Handle;
            }
            // Rename internal 'suggestions' back to what backend might expect if it differs,
            // but user explicitly said 'suggestions' array.
            // If backend also wants 'cardContents', we can wrap it here, but let's stick to the card structure.
            if (newCard.videoS3Url) {
                newCard.videoContent = newCard.videoS3Url;
                newCard.videoHandle = newCard.videoS3Handle;
                delete newCard.videoFile;
                delete newCard.videoPreviewUrl;
                delete newCard.videoS3Url;
                delete newCard.videoS3Handle;
            }
            if (newCard.videoThumbnailS3Url) {
                newCard.videoThumbnailContent = newCard.videoThumbnailS3Url;
                delete newCard.videoThumbnailFile;
                delete newCard.videoThumbnailS3Url;
            }
            return newCard;
        });

        const tempData = {
            templateType,
            botName,
            templateName,
            ttl,
            orientation,
            cardHeight,
            cardWidth,
            cards: processedCards
        };

        const params = {
            ENTITY: "FORMPROCESS",
            OPERATION: "saveRCSTemplate",
            tempData: tempData
        };

        try {
            const response = await requestApi.processServerRequest(params);
            if (response && response.issuccess) {
                showToast("Template saved successfully");
                onCancel();
            } else {
                showToast(response?.errormsg || "Failed to save template", "top-right", "error");
            }
        } catch (error) {
            console.error("Save error:", error);
            showToast("An error occurred while saving", "top-right", "error");
        }
    };

    // Effect to reset/init cards when template type changes
    useEffect(() => {
            if (templateType === 'Carousel Card') {
                if (cards.length < 2) {
                    setCards([
                        { mediaType: 'image', title: '', body: '', suggestions: [], imageFile: null, imageFileName: '', videoFile: null, videoFileName: '', videoThumbnailFile: null, videoThumbnailFileName: '' },
                        { mediaType: 'image', title: '', body: '', suggestions: [], imageFile: null, imageFileName: '', videoFile: null, videoFileName: '', videoThumbnailFile: null, videoThumbnailFileName: '' }
                    ]);
                    setActiveCardIndex(0);
                }
            } else {
            // Reset to single card if switching back, or keep first
            if (cards.length > 1) {
                setCards([cards[0]]);
                setActiveCardIndex(0);
            }
        }
    }, [templateType]);
    
    // Cleanup restricted buttons when Title/Body changes
    useEffect(() => {
        const currentCard = cards[activeCardIndex];
        if (!currentCard) return;

        const hasVar = /\[custom_param_text_\d+\]/.test(currentCard.title || "") || 
                       /\[custom_param_desc_\d+\]/.test(currentCard.body || "");

        if (hasVar) {
            const restrictedTypes = templateType === 'Standard Card' ? ['Reply', 'Dialer Action'] : ['Reply'];
            const needsCleanup = currentCard.suggestions.some(btn => restrictedTypes.includes(btn.type));
            
            if (needsCleanup) {
                const newCards = [...cards];
                newCards[activeCardIndex].suggestions = currentCard.suggestions.filter(btn => !restrictedTypes.includes(btn.type));
                setCards(newCards);
            }
        }
    }, [cards[activeCardIndex]?.title, cards[activeCardIndex]?.body, templateType]);


    const renderButtonFields = (btn, index) => {
        return (
            <div key={index} className="border p-3 mb-3 rounded position-relative bg-white">
                <div
                    className="position-absolute text-danger p-0"
                    style={{ top: '10px', right: '10px', fontSize: '1.5rem', cursor: 'pointer', lineHeight: '1' }}
                    onClick={() => removeButton(index)}
                    role="button"
                    tabIndex={0}
                >
                    <span aria-hidden="true">&times;</span>
                </div>

                <Row className="form-row">
                    <Col md={4}>
                        <FormGroup>
                            <Label className="small fw-bold">Type of Action</Label>
                             <Input
                                type="select"
                                bsSize="sm"
                                value={btn.type}
                                onChange={(e) => updateButton(index, 'type', e.target.value)}
                            >
                                {(() => {
                                    const hasVar = /\[custom_param_text_\d+\]/.test(currentCard.title || "") || 
                                                   /\[custom_param_desc_\d+\]/.test(currentCard.body || "");
                                    const options = [];
                                    
                                    // Reply
                                    if (!(hasVar)) {
                                        options.push(<option key="Reply">Reply</option>);
                                    }
                                    
                                    // Dialer Action
                                    if (!(templateType === 'Standard Card' && hasVar)) {
                                        options.push(<option key="Dialer">Dialer Action</option>);
                                    }
                                    
                                    options.push(<option key="URL">URL Action</option>);
                                    options.push(<option key="LatLong">View Location (Lat/Long)</option>);
                                    options.push(<option key="Query">View Location (Query)</option>);
                                    options.push(<option key="Share">Share Location</option>);
                                    options.push(<option key="Calendar">Create Calendar Event</option>);
                                    
                                    return options;
                                })()}
                            </Input>
                            {(() => {
                                const hasVar = /\[custom_param_text_\d+\]/.test(currentCard.title || "") || 
                                               /\[custom_param_desc_\d+\]/.test(currentCard.body || "");
                                return hasVar && (
                                    <div className="text-muted x-small mt-1" style={{ fontSize: '10px' }}>
                                        <i className="fa fa-info-circle me-1"></i>
                                        Action buttons are not supported when variables are used
                                    </div>
                                );
                            })()}
                        </FormGroup>
                    </Col>

                    <Col md={8}>
                        <FormGroup>
                            <Label className="small fw-bold">Text</Label>
                            <div className="d-flex justify-content-between">
                                <Input
                                    bsSize="sm"
                                    placeholder="Enter text"
                                    value={btn.text || ''}
                                    onChange={(e) => updateButton(index, 'text', e.target.value)}
                                    maxLength={25}
                                    invalid={!!errors[`btn_text_${activeCardIndex}_${index}`]}
                                />
                                <small className="text-muted ms-2 align-self-center">{(btn.text || '').length}/25</small>
                            </div>
                            {errors[`btn_text_${activeCardIndex}_${index}`] && <div className="text-danger small">{errors[`btn_text_${activeCardIndex}_${index}`]}</div>}
                        </FormGroup>
                    </Col>
                </Row>

                {btn.type === 'Dialer Action' && (
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label className="small fw-bold">Country Code</Label>
                                <Input type="select" bsSize="sm" value={btn.countryCode || '+91'} onChange={(e) => updateButton(index, 'countryCode', e.target.value)}>
                                    <option>+91</option><option>+1</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={8}>
                            <FormGroup>
                                <Label className="small fw-bold">Phone number</Label>
                                <Input bsSize="sm" placeholder="Enter number" value={btn.phoneNumber || ''} onChange={(e) => updateButton(index, 'phoneNumber', e.target.value)} maxLength={15} />
                            </FormGroup>
                        </Col>
                    </Row>
                )}
                {btn.type === 'URL Action' && (
                    <Row><Col md={12}><FormGroup><Label className="small fw-bold">URL/URI to open</Label><Input bsSize="sm" placeholder="Enter URL" value={btn.url || ''} onChange={(e) => updateButton(index, 'url', e.target.value)} maxLength={2048} /></FormGroup></Col></Row>
                )}
                {btn.type === 'View Location (Lat/Long)' && (
                    <Row>
                        <Col md={6}><FormGroup><Label className="small fw-bold">Latitude</Label><Input bsSize="sm" value={btn.latitude || ''} onChange={(e) => updateButton(index, 'latitude', e.target.value)} /></FormGroup></Col>
                        <Col md={6}><FormGroup><Label className="small fw-bold">Longitude</Label><Input bsSize="sm" value={btn.longitude || ''} onChange={(e) => updateButton(index, 'longitude', e.target.value)} /></FormGroup></Col>
                        <Col md={12}><FormGroup><Label className="small fw-bold">Label</Label><Input bsSize="sm" value={btn.label || ''} onChange={(e) => updateButton(index, 'label', e.target.value)} /></FormGroup></Col>
                    </Row>
                )}
                {btn.type === 'View Location (Query)' && (
                    <Row><Col md={12}><FormGroup><Label className="small fw-bold">Query</Label><Input bsSize="sm" value={btn.query || ''} onChange={(e) => updateButton(index, 'query', e.target.value)} /></FormGroup></Col></Row>
                )}
                {btn.type === 'Create Calendar Event' && (
                    <Row>
                        <Col md={12}><FormGroup><Label className="small fw-bold">Event Date and Time</Label><Input type="datetime-local" bsSize="sm" value={btn.eventTime || ''} onChange={(e) => updateButton(index, 'eventTime', e.target.value)} /></FormGroup></Col>
                        <Col md={6}><FormGroup><Label className="small fw-bold">Event Title</Label><Input bsSize="sm" value={btn.eventTitle || ''} onChange={(e) => updateButton(index, 'eventTitle', e.target.value)} /></FormGroup></Col>
                        <Col md={6}><FormGroup><Label className="small fw-bold">Description</Label><Input bsSize="sm" value={btn.description || ''} onChange={(e) => updateButton(index, 'description', e.target.value)} /></FormGroup></Col>
                    </Row>
                )}
            </div>
        );
    };

    const getButtonIcon = (type) => {
        switch (type) {
            case 'Dialer Action': return 'fa fa-phone';
            case 'URL Action': return 'fa fa-globe';
            case 'View Location (Lat/Long)':
            case 'View Location (Query)': return 'fa fa-map-marker';
            case 'Share Location': return 'fa fa-map-pin';
            case 'Create Calendar Event': return 'fa fa-calendar';
            default: return 'fa fa-reply';
        }
    };

    // Preview Logic
    const currentCard = cards[activeCardIndex];
    if (!currentCard) return null;

    return (
        <div className="kv-main-wrapper h100per w-100">
            <Container fluid>
                <div className="app-page-title">
                    <div className="page-title-wrapper">
                        <div className="page-title-heading">
                            <div className="page-title-icon">
                                <i className="pe-7s-plus icon-gradient bg-grow-early"></i>
                            </div>
                            <div>New Message Template
                                <div className="page-title-subheading">
                                    RCS Configuration &gt; One By One Addition
                                </div>
                            </div>
                        </div>
                        <div className="page-title-actions">
                            <Button color="success" className="me-2 rounded-pill px-4" onClick={onCancel}>Cancel</Button>
                            <Button color="success" className="rounded-pill px-4" onClick={handleSave}>Save & Close</Button>
                        </div>
                    </div>
                </div>

                <Card className="main-card mb-3">
                    <CardBody>
                        <Row>
                            <Col md={7}>
                                {/* <FormGroup>
                                    <Label className="fw-bold">Username</Label>
                                    <div className="small text-muted mb-2">Please select the user for whom you would like to create a template</div>
                                    <Input type="select">
                                        <option>Grand_Tour2</option>
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Label className="fw-bold">Bot Name and Type</Label>
                                    <div className="small text-muted mb-2">Enter the Bot Name and Type</div>
                                    
                                    <Input
                                        type="text"
                                        placeholder="Enter Bot Name and Type"
                                        value={botName}
                                        onChange={(e) => setBotName(e.target.value)}
                                    />
                                </FormGroup> */}
                                <FormGroup>
                                    <Label className="fw-bold">Template Name</Label>
                                    <div className="small text-muted mb-2">Name your template using lowercase letters, alphanumeric characters, and underscores</div>
                                    <Input
                                        placeholder="Enter template name"
                                        value={templateName}
                                        onChange={(e) => setTemplateName(e.target.value)}
                                        invalid={!!errors.templateName}
                                    />
                                    <div className="d-flex justify-content-between">
                                        <small className="text-danger">{errors.templateName}</small>
                                        <div className="small text-muted">{(templateName || '').length}/20</div>
                                    </div>
                                </FormGroup>

                                <FormGroup>
                                    <Label className="fw-bold">Template Type</Label>
                                    <div className="small text-muted mb-2">Choose a message template</div>
                                    <Input
                                        type="select"
                                        value={templateType}
                                        onChange={(e) => setTemplateType(e.target.value)}
                                    >
                                        <option>Standard Card</option>
                                        <option>Rich Media Card</option>
                                        <option>Carousel Card</option>
                                    </Input>
                                </FormGroup>

                                <FormGroup>
                                    <Label className="fw-bold">TTL</Label>
                                    <div className="small text-muted mb-2">Set the time-to-live for the template</div>
                                    <div className="input-group">
                                        <Input
                                            placeholder="Enter TTL value"
                                            value={ttl}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setTtl(val);
                                                let newErrors = { ...errors };
                                                if (val && (parseInt(val) < 600 || parseInt(val) > 3600)) {
                                                    newErrors.ttl = 'TTL value must be between 600 and 3600 seconds';
                                                } else {
                                                    delete newErrors.ttl;
                                                }
                                                setErrors(newErrors);
                                            }}
                                            invalid={!!errors.ttl}
                                        />
                                        <div className="input-group-append">
                                            <span className="input-group-text text-white bg-success">Seconds</span>
                                        </div>
                                    </div>
                                    {errors.ttl && <small className="text-danger d-block mt-1">{errors.ttl}</small>}
                                </FormGroup>

                                {/* Carousel Specific: Card Selection */}
                                {templateType === 'Carousel Card' && (
                                    <FormGroup>
                                        <Label className="fw-bold">Cards</Label>
                                        <div className="small text-muted mb-2">A carousel can have a minimum of 2 cards and a maximum 10 cards.</div>
                                        <div className="d-flex flex-wrap gap-2">
                                            {cards.map((_, idx) => (
                                                <Button
                                                    key={idx}
                                                    color={activeCardIndex === idx ? "success" : "light"}
                                                    className="border"
                                                    size="sm"
                                                    onClick={() => setActiveCardIndex(idx)}
                                                >
                                                    Card {idx + 1}
                                                    {cards.length > 2 && (
                                                        <span
                                                            className="ms-2 text-dark"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={(e) => handleRemoveCard(idx, e)}
                                                        >&times;</span>
                                                    )}
                                                </Button>
                                            ))}
                                            {cards.length < 10 && (
                                                <Button color="success" size="sm" onClick={handleAddCard} className="px-3">
                                                    <i className="fa fa-plus me-1"></i> Add Card
                                                </Button>
                                            )}
                                        </div>
                                    </FormGroup>
                                )}

                                {/* Carousel Specific: Width */}
                                {templateType === 'Carousel Card' && (
                                    <FormGroup>
                                        <Label className="fw-bold">Card Width</Label>
                                        <div className="small text-muted mb-2">Cards in the carousel are vertical. Select the width of the card for your template.</div>
                                        <div>
                                            <div className="form-check form-check-inline">
                                                <Input type="radio" name="cardWidth" checked={cardWidth === 'small'} onChange={() => setCardWidth('small')} className="form-check-input rcs-radio-sm" />
                                                <Label className="form-check-label">Small</Label>
                                            </div>
                                            <div className="form-check form-check-inline">
                                                <Input type="radio" name="cardWidth" checked={cardWidth === 'medium'} onChange={() => setCardWidth('medium')} className="form-check-input rcs-radio-sm" />
                                                <Label className="form-check-label">Medium</Label>
                                            </div>
                                        </div>
                                    </FormGroup>
                                )}

                                {/* Rich Media Specific: Orientation */}
                                {templateType === 'Rich Media Card' && (
                                    <FormGroup>
                                        <Label className="fw-bold">Card Orientation</Label>
                                        <div className="small text-muted mb-2">Vertical rich cards display horizontal media at the top of the card. Horizontal rich cards display vertical media at on the left or right side of the card.</div>
                                        <div>
                                            <div className="form-check form-check-inline">
                                                <Input type="radio" name="orientation" checked={orientation === 'vertical'} onChange={() => setOrientation('vertical')} className="form-check-input rcs-radio-sm" />
                                                <Label className="form-check-label">Vertical</Label>
                                            </div>
                                            <div className="form-check form-check-inline">
                                                <Input type="radio" name="orientation" checked={orientation === 'horizontal'} onChange={() => setOrientation('horizontal')} className="form-check-input rcs-radio-sm" />
                                                <Label className="form-check-label">Horizontal</Label>
                                            </div>
                                        </div>
                                    </FormGroup>
                                )}

                                {/* Card Height - Available for Rich and Carousel */}
                                {(templateType === 'Rich Media Card' || templateType === 'Carousel Card') && (
                                    <FormGroup>
                                        <Label className="fw-bold">Card Height</Label>
                                        <div className="small text-muted mb-2">Card must fit one of two heights.</div>
                                        <div>
                                            <div className="form-check form-check-inline">
                                                <Input type="radio" name="cardHeight" checked={cardHeight === 'short'} onChange={() => setCardHeight('short')} className="form-check-input rcs-radio-sm" />
                                                <Label className="form-check-label">Short</Label>
                                            </div>
                                            <div className="form-check form-check-inline">
                                                <Input type="radio" name="cardHeight" checked={cardHeight === 'medium'} onChange={() => setCardHeight('medium')} className="form-check-input rcs-radio-sm" />
                                                <Label className="form-check-label">Medium</Label>
                                            </div>
                                        </div>
                                    </FormGroup>
                                )}

                                <div className="border-bottom my-4"></div>

                                {/* Content Fields - Work on currentCard */}
                                {(templateType === 'Rich Media Card' || templateType === 'Carousel Card') && (

                                    <FormGroup>
                                        <Label className="fw-bold">Header</Label>
                                        <div className="small text-muted mb-2">Choose an option below to configure media type</div>
                                        <div>
                                            <div className="form-check form-check-inline">
                                                <Input
                                                    type="radio"
                                                    name={`mediaType-${activeCardIndex}`} // unique name per render
                                                    checked={currentCard.mediaType === 'image'}
                                                    onChange={() => updateCurrentCard('mediaType', 'image')}
                                                    className="form-check-input rcs-radio-sm"
                                                />
                                                <Label className="form-check-label">Image</Label>
                                            </div>
                                            <div className="form-check form-check-inline">
                                                <Input
                                                    type="radio"
                                                    name={`mediaType-${activeCardIndex}`}
                                                    checked={currentCard.mediaType === 'video'}
                                                    onChange={() => updateCurrentCard('mediaType', 'video')}
                                                    className="form-check-input rcs-radio-sm"
                                                />
                                                <Label className="form-check-label">Video</Label>
                                            </div>
                                        </div>
                                    </FormGroup>
                                )}
                                {((templateType === 'Rich Media Card' || templateType === 'Carousel Card') && currentCard.mediaType === 'video') && (
                                    <>
                                        <FormGroup>
                                            <Label className="fw-bold">Upload Video</Label>
                                            <div className="small text-muted mb-2">The video you specify must have a max file size of 5 MB, have a resolution of 1440 x 480 pixels and should be a MP4.</div>
                                            <div className="d-flex align-items-center">
                                                <Input
                                                    type="file"
                                                    style={{ display: 'none' }}
                                                    id={`videoFile-${activeCardIndex}`}
                                                    onChange={(e) => handleFileChange(activeCardIndex, 'video', e)}
                                                    accept="video/mp4"
                                                />
                                                <Label htmlFor={`videoFile-${activeCardIndex}`} className="btn btn-success rounded-pill px-4">
                                                    Choose
                                                </Label>
                                                <span className="ms-2 small text-muted">
                                                    {currentCard.videoFileName || 'No file chosen'}
                                                </span>
                                            </div>
                                        </FormGroup>
                                        <FormGroup>
                                            <Label className="fw-bold">Video Thumbnail</Label>
                                            <div className="small text-muted mb-2">The thumbnail you specify must have a max file size of 100KB, have a resolution of 1440 x 480 pixels and should be a JPEG, JPG, PNG.</div>
                                            <div className="d-flex align-items-center">
                                                <Input
                                                    type="file"
                                                    style={{ display: 'none' }}
                                                    id={`videoThumbnail-${activeCardIndex}`}
                                                    onChange={(e) => handleFileChange(activeCardIndex, 'videoThumbnail', e)}
                                                    accept="image/jpeg, image/jpg, image/png"
                                                />
                                                <Label htmlFor={`videoThumbnail-${activeCardIndex}`} className="btn btn-success rounded-pill px-4">
                                                    Choose
                                                </Label>
                                                <span className="ms-2 small text-muted">
                                                    {currentCard.videoThumbnailFileName || 'No file chosen'}
                                                </span>
                                            </div>
                                        </FormGroup>
                                    </>
                                )}
                                {((templateType === 'Rich Media Card' || templateType === 'Carousel Card') && currentCard.mediaType === 'image') && (
                                    <FormGroup>
                                        <Label className="fw-bold">Upload Image</Label>
                                        <div className="small text-muted mb-2">The image you specify must have a max file size of 1 MB and match the required resolution for the selected card style.</div>
                                        <div className="d-flex align-items-center">
                                            <Input
                                                type="file"
                                                style={{ display: 'none' }}
                                                id={`customFile-${activeCardIndex}`} // Unique ID
                                                onChange={(e) => handleFileChange(activeCardIndex, 'image', e)}
                                                accept="image/jpeg, image/jpg, image/png, image/gif"
                                            />
                                            <Label htmlFor={`customFile-${activeCardIndex}`} className="btn btn-success rounded-pill px-4">
                                                Choose File
                                            </Label>
                                            <span className="ms-2 small text-muted">
                                                {currentCard.imageFileName || 'No file chosen'}
                                            </span>
                                        </div>
                                        {errors[`image_${activeCardIndex}`] && <div className="text-danger small mt-1">{errors[`image_${activeCardIndex}`]}</div>}
                                    </FormGroup>
                                )}

                                {templateType !== 'Standard Card' && (
                                    <FormGroup>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <Label className="fw-bold mb-0">Title</Label>
                                            <div className="d-flex align-items-center">
                                                <div className="small text-muted me-3">{(currentCard.title || '').length}/200</div>
                                                <span
                                                    className="text-primary small fw-bold"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => addVariable('title')}
                                                >
                                                    + Variable
                                                </span>
                                            </div>
                                        </div>
                                        <div className="small text-muted mb-2">Add a title for your card</div>
                                        <Input
                                            innerRef={titleInputRef}
                                            placeholder="Enter the title"
                                            value={currentCard.title || ''}
                                            onChange={(e) => updateCurrentCard('title', e.target.value)}
                                            onClick={handleCursorPosition}
                                            onKeyUp={handleCursorPosition}
                                            invalid={!!errors[`title_${activeCardIndex}`]}
                                        />
                                        {errors[`title_${activeCardIndex}`] && <div className="text-danger small mt-1">{errors[`title_${activeCardIndex}`]}</div>}
                                    </FormGroup>
                                )}

                                <FormGroup>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Label className="fw-bold mb-0">Body</Label>
                                        <div className="d-flex align-items-center">
                                            <div className="small text-muted me-3">{(currentCard.body || '').length}/{templateType === 'Standard Card' ? 2500 : 2000}</div>
                                            <span
                                                className="text-primary small fw-bold"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => addVariable('body')}
                                            >
                                                + Variable
                                            </span>
                                        </div>
                                    </div>
                                    <div className="small text-muted mb-2">Enter the text for your message</div>
                                    <Input
                                        innerRef={bodyInputRef}
                                        type="textarea"
                                        placeholder="Enter the body text"
                                        rows="4"
                                        value={currentCard.body || ''}
                                        onChange={(e) => updateCurrentCard('body', e.target.value)}
                                        onClick={handleCursorPosition}
                                        onKeyUp={handleCursorPosition}
                                        invalid={!!errors[`body_${activeCardIndex}`]}
                                        maxLength={templateType === 'Standard Card' ? 2500 : 2000}
                                    />
                                    {errors[`body_${activeCardIndex}`] && <div className="text-danger small mt-1">{errors[`body_${activeCardIndex}`]}</div>}
                                </FormGroup>

                                <FormGroup>
                                    <Label className="fw-bold">Call to Action/Reply Buttons <span className="badge badge-secondary">Optional</span></Label>
                                    <div className="small text-muted mb-2">Create a Call to Action or Reply Buttons. You can add upto 3 buttons</div>

                                    {currentCard.suggestions.map((btn, index) => renderButtonFields(btn, index))}

                                    {currentCard.suggestions.length < 3 && (
                                        <Button color="light" className="border" onClick={addButton}>+ Add Button</Button>
                                    )}
                                </FormGroup>

                            </Col>

                            {/* Preview Column */}
                            <Col md={1} className="d-flex justify-content-center">
                                <div className="border-start h-100"></div>
                            </Col>
                            <Col md={4}>
                                <div className="sticky-top" style={{ top: '20px' }}>
                                    <h5 className="fw-bold mb-3">Preview</h5>
                                    <div className="d-flex flex-column align-items-center">

                                        {templateType === 'Carousel Card' ? (
                                            <div className="d-flex align-items-center w-100 justify-content-center">
                                                <Button color="link" className="text-success" disabled={activeCardIndex === 0} onClick={() => setActiveCardIndex(activeCardIndex - 1)}>
                                                    <i className="fa fa-chevron-left fa-2x"></i>
                                                </Button>
                                                <div className="text-center">
                                                    <div className="text-success small fw-bold mb-1">CARD {activeCardIndex + 1}</div>
                                                    <div className="bg-light rounded p-3 overflow-hidden shadow-sm position-relative" style={{ width: cardWidth === 'medium' ? '280px' : '200px', display: 'flex', flexDirection: 'column' }}>
                                                        {currentCard.imageS3Url || currentCard.imagePreviewUrl ? (
                                                            <div className="w-100 mb-2" style={{ height: '120px', overflow: 'hidden' }}>
                                                                <img src={currentCard.imageS3Url || currentCard.imagePreviewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            </div>
                                                        ) : currentCard.videoS3Url || currentCard.videoPreviewUrl ? (
                                                            <div className="w-100 mb-2" style={{ height: '120px', overflow: 'hidden' }}>
                                                                <video src={currentCard.videoS3Url || currentCard.videoPreviewUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            </div>
                                                        ) : null}
                                                        <div className="p-2 flex-grow-1 bg-white text-start">
                                                            <h6 className="fw-bold mb-1">{currentCard.title || ""}</h6>
                                                            <p className="small mb-0" style={{whiteSpace:'pre-wrap', fontSize:'13px'}}>{currentCard.body || ""}</p>
                                                        </div>
                                                        <div className="mt-auto border-top">
                                                            {currentCard.suggestions && currentCard.suggestions.map((btn, index) => (
                                                                <div key={index} className="p-2 text-center text-success d-flex justify-content-center align-items-center bg-white border-bottom" style={{ cursor: 'pointer' }}>
                                                                    <i className={`${getButtonIcon(btn.type)} me-2`}></i>
                                                                    <span className="small fw-bold">{btn.text || 'Button Text'}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                <Button color="link" className="text-success" disabled={activeCardIndex === cards.length - 1} onClick={() => setActiveCardIndex(activeCardIndex + 1)}>
                                                    <i className="fa fa-chevron-right fa-2x"></i>
                                                </Button>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="bg-light rounded p-3 overflow-hidden shadow-sm position-relative" style={{ width: '320px', display: 'flex', flexDirection: 'column' }}>
                                                    <div className="flex-grow-1 d-flex flex-column bg-white">
                                                        {templateType !== 'Standard Card' && (currentCard.imagePreviewUrl || currentCard.imageS3Url) ? (
                                                            <div className="w-100 mb-2" style={{ height: '120px', overflow: 'hidden' }}>
                                                                <img src={currentCard.imageS3Url || currentCard.imagePreviewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            </div>
                                                        ) : templateType !== 'Standard Card' && (currentCard.videoPreviewUrl || currentCard.videoS3Url) ? (
                                                            <div className="w-100 mb-2" style={{ height: '120px', overflow: 'hidden' }}>
                                                                <video src={currentCard.videoS3Url || currentCard.videoPreviewUrl} controls style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            </div>
                                                        ) : null}
                                                        <div className="p-3 flex-grow-1 bg-white text-start">
                                                            {templateType !== 'Standard Card' && (
                                                                <h6 className="fw-bold mb-1">{currentCard.title || ""}</h6>
                                                            )}
                                                            <p className="small mb-0" style={{whiteSpace:'pre-wrap', fontSize:'13px'}}>{currentCard.body || ""}</p>
                                                        </div>
                                                        <div className="mt-auto border-top">
                                                            {currentCard.suggestions && currentCard.suggestions.map((btn, index) => (
                                                                <div key={index} className="p-2 text-center text-success d-flex justify-content-center align-items-center bg-white border-bottom" style={{ cursor: 'pointer' }}>
                                                                    <i className={`${getButtonIcon(btn.type)} me-2`}></i>
                                                                    <span className="small fw-bold">{btn.text || 'Button Text'}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};
export default CreateRCSTemplate;
